<template>
  <wz-modal
    :value="visible"
    :title="`Asistencia — ${employeeName} — ${recordDate}`"
    size="md"
    @close="$emit('close')"
  >
    <wz-alert v-if="dayData && dayData.isAutoDetected" variant="warning" icon="ri-information-line" class="mb-3">
      Este día fue marcado automáticamente como <strong>{{ autoLabel }}</strong>. Puede modificarlo.
    </wz-alert>

    <form @submit.prevent="save" novalidate>

      <!-- Estado -->
      <div class="mb-3">
        <label class="label-form">Estado <span class="text-danger">*</span></label>
        <select class="wz-form-control" v-model="form.attendanceStatus" required>
          <option value="">Seleccionar estado</option>
          <option v-for="s in statusOptions" :key="s.key" :value="s.key">{{ s.label }}</option>
        </select>
        <div v-if="submitted && !form.attendanceStatus" class="form-error">El estado es requerido</div>
      </div>

      <!-- Goce de haber (solo FALTA_JUSTIFICADA): determina si el día se paga y si computa para beneficios -->
      <div v-if="form.attendanceStatus === 'FALTA_JUSTIFICADA'" class="mb-3">
        <label class="label-form flex items-center">
          <input type="checkbox" class="mr-2" v-model="form.justifiedPaid" />
          Con goce de haber
        </label>
        <small class="text-muted">
          Con goce: el día se paga y computa (no descuenta ni reduce gratificación/CTS).
          Sin goce: descuenta y no computa.
        </small>
      </div>

      <!-- Horas (solo si ASISTIO) -->
      <template v-if="form.attendanceStatus === 'ASISTIO'">
        <div class="flex flex-wrap -mx-2">
          <div class="w-1/2">
            <div class="mb-3">
              <label class="label-form">Hora de entrada</label>
              <input type="time" class="wz-form-control" v-model="form.entryTime" @change="recalculate" />
            </div>
          </div>
          <div class="w-1/2">
            <div class="mb-3">
              <label class="label-form">Hora de salida</label>
              <input type="time" class="wz-form-control" v-model="form.exitTime" @change="recalculate" />
            </div>
          </div>
        </div>

        <div v-if="calculation" class="calc-preview mb-3">
          <i class="ri-time-line mr-1 text-primary"></i>
          <span v-if="calculation.tardiness !== null">Tardanza: <strong>{{ calculation.tardiness }} min</strong></span>
          <span v-if="calculation.hoursWorked !== null" class="ml-3">Horas trabajadas: <strong>{{ calculation.hoursWorked }}h</strong></span>
        </div>
      </template>

      <!-- Observaciones -->
      <div class="mb-3">
        <label class="label-form">Observaciones</label>
        <textarea class="wz-form-control" v-model="form.observations" rows="2" placeholder="Opcional..."></textarea>
      </div>

      <!-- Confirmación de propagación de feriado. Cada fila ya es una relación laboral concreta
           (una empresa), así que se propaga a la empresa de la fila. -->
      <div v-if="holidayConfirm" class="holiday-confirm mt-3">
        <wz-alert variant="info" icon="ri-calendar-event-line" class="mb-3">
          ¿Desea marcar este feriado para todos los colaboradores de
          <strong>{{ companyName || 'la empresa' }}</strong>
          el día <strong v-local-date="recordDate"></strong>?
        </wz-alert>

        <div class="flex justify-end">
          <wz-button variant="light" class="mr-2" :disabled="propagating || saving" @click="holidayConfirm = false">
            Volver
          </wz-button>
          <wz-button variant="outline" class="mr-2" :disabled="propagating || saving" @click="onlyThisCollaborator">
            Solo este colaborador
          </wz-button>
          <wz-button variant="primary" :disabled="propagating || !companyId" @click="applyToAll">
            <span v-if="propagating"><i class="ri-loader-4-line"></i> Aplicando...</span>
            <span v-else>Sí, aplicar a todos</span>
          </wz-button>
        </div>
      </div>

      <div v-else class="flex justify-end mt-3">
        <wz-button variant="light" class="mr-2" @click="$emit('close')">Cancelar</wz-button>
        <wz-button type="submit" variant="primary" :disabled="saving">
          <span v-if="saving"><i class="ri-loader-4-line"></i> Guardando...</span>
          <span v-else>Guardar</span>
        </wz-button>
      </div>

    </form>
  </wz-modal>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { AttendanceService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'AttendanceEditModal',

  props: {
    visible:      { type: Boolean, default: false },
    employeeName: { type: String,  default: '' },
    employeeId:   { type: Number,  default: null },
    recordDate:   { type: String,  default: '' },
    dayData:      { type: Object,  default: null },
    // Empresa de la fila (relación laboral concreta): destino de guardado y de propagación de feriado.
    companyId:    { type: [Number, String], default: null },
    companyName:  { type: String, default: '' },
  },

  data() {
    return {
      saving:      false,
      propagating: false,
      holidayConfirm: false,
      submitted: false,
      form: {
        attendanceStatus: '',
        justifiedPaid:    false,
        entryTime:        '',
        exitTime:         '',
        observations:     '',
      },
      statusOptions: Object.values(Constants.AttendanceStatus),
      calculation: null,
    }
  },

  computed: {
    autoLabel() {
      if (!this.dayData) return ''
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === this.dayData.status)
      return found ? found.label : this.dayData.status
    },
  },

  watch: {
    visible(val) {
      if (val) this.init()
    },
  },

  methods: {
    init() {
      this.submitted = false
      this.holidayConfirm = false
      this.propagating = false
      this.calculation = null
      if (this.dayData) {
        this.form.attendanceStatus = this.dayData.status    || ''
        this.form.justifiedPaid    = this.dayData.justifiedPaid || false
        this.form.entryTime        = this.dayData.entryTime || ''
        this.form.exitTime         = this.dayData.exitTime  || ''
        this.form.observations     = this.dayData.observations || ''
      } else {
        this.form = { attendanceStatus: '', justifiedPaid: false, entryTime: '', exitTime: '', observations: '' }
      }
    },

    recalculate() {
      const { entryTime, exitTime } = this.form
      if (!entryTime || !exitTime) { this.calculation = null; return }

      const toMin = t => {
        const [h, m] = t.split(':').map(Number)
        return h * 60 + m
      }
      const diff = toMin(exitTime) - toMin(entryTime)
      if (diff <= 0) { this.calculation = null; return }

      this.calculation = {
        hoursWorked: (diff / 60).toFixed(2),
        tardiness:   null,
      }
    },

    save() {
      this.submitted = true
      if (!this.form.attendanceStatus) return
      // Feriado: antes de guardar, ofrecer propagar a toda la empresa.
      if (this.form.attendanceStatus === 'FERIADO') {
        this.holidayConfirm = true
        return
      }
      this.persistSingle()
    },

    // Guarda el registro solo para este colaborador (comportamiento base).
    async persistSingle() {
      try {
        this.saving = true
        await AttendanceService.saveRecord({
          employeeId:       this.employeeId,
          companyId:        this.companyId || null, // multi-empresa: guarda en la empresa de la fila
          recordDate:       this.recordDate,
          attendanceStatus: this.form.attendanceStatus,
          // Goce solo aplica a FALTA_JUSTIFICADA; el backend igual lo normaliza.
          justifiedPaid:    this.form.attendanceStatus === 'FALTA_JUSTIFICADA' ? this.form.justifiedPaid : false,
          entryTime:        this.form.entryTime   || null,
          exitTime:         this.form.exitTime     || null,
          observations:     this.form.observations || null,
        })
        Alert.success('Asistencia guardada correctamente')
        this.$emit('saved')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar la asistencia')
      } finally {
        this.saving = false
      }
    },

    // "Solo este colaborador": guarda el feriado individual, sin propagar.
    onlyThisCollaborator() {
      this.persistSingle()
    },

    // "Sí, aplicar a todos": propaga el feriado a los colaboradores activos de la empresa de la fila.
    async applyToAll() {
      if (!this.companyId) return
      try {
        this.propagating = true
        const resp = await AttendanceService.propagateHoliday({
          companyId:    this.companyId,
          date:         this.recordDate,
          observations: this.form.observations || null,
        })
        this.$emit('propagated', resp.payload)
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al propagar el feriado')
      } finally {
        this.propagating = false
      }
    },
  },

  created() {
    if (this.visible) this.init()
  },
}
</script>

<style scoped lang="stylus">
.calc-preview
  background #f8f9fa
  border 1px solid #dee2e6
  border-radius 4px
  padding 8px 12px
  font-size 13px

.alert-sm
  padding 8px 12px
  font-size 13px
</style>
